// @givmo/fundraisers-module — a campaign's layers (FCM-WEB25, plan §FC-16): the reads the Layers tab and
// the Campaign tab's cards make, each a GENERATED op over the owner-agnostic `/fundraisers/{id}/campaign…`
// family (FCM-BE31, FCM-BE33, FCM-BE34, FCM-BE56, FCM-BE63) or the public campaign wire. Every one of them
// is dark while the backend's CAMPAIGN_LAYERS_ENABLED is off: the route answers the absent-route 404, so a
// failed read is the page's "not served" and never an error to show.
import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  extractList,
  isGivmoError,
  sdk,
  type FundraiserOut,
  type FundraisersGetFundraiserError,
  type P2pCampaignOut,
  type P2pLayerCandidateOut,
  type P2pLayerInviteOut,
  type P2pLayerNodeOut,
  type P2pLayerParentOut,
  type P2pLayerStructureOut,
  type P2pLeaderboardOut,
  type P2pScheduleRowOut,
} from '@givmo/api-client';
import {
  campaignsGetCampaignLayerParentQueryKey,
  campaignsGetCampaignLayerStructureQueryKey,
  campaignsListCampaignLayerCandidatesQueryKey,
  campaignsListCampaignScheduleQueryKey,
  campaignsListCampaignStructureLayersQueryKey,
  campaignsListReceivedCampaignLayerInvitesQueryKey,
  campaignsListSentCampaignLayerInvitesQueryKey,
  fundraisersGetFundraiserOptions,
  fundraisersGetFundraiserQueryKey,
  p2pGetCampaignP2pQueryKey,
  p2pGetLeaderboardQueryKey,
} from '@givmo/api-client/query';

/** The tree's page, and the most a node's page may ask for (FCM-BE63's `TREE_PAGE_SIZE`). */
export const LAYER_PAGE = 10;
/** The deepest a campaign may sit, the top counted (FCM-BE31's `MAX_LAYER_DEPTH`): nothing goes under it. */
export const MAX_LAYER_DEPTH = 5;
/** The candidates read's page, at most (FCM-BE63's `CANDIDATE_PAGE_LIMIT`). */
export const CANDIDATE_PAGE = 25;
/** The layer-invite lists' page, at most (FCM-BE34's `LAYER_INVITE_PAGE_LIMIT`). */
export const INVITE_PAGE = 25;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNode(value: unknown): value is P2pLayerNodeOut {
  return (
    isRecord(value) &&
    typeof value.campaign_id === 'string' &&
    typeof value.depth === 'number' &&
    typeof value.ours === 'boolean' &&
    typeof value.can_manage === 'boolean'
  );
}

/** Where the campaign sits (FCM-BE31's parent read): its ancestors, top first, and whether it may leave. */
export function layerParentOptions(campaignId: string): UseQueryOptions<P2pLayerParentOut, Error> {
  const options = { path: { fundraiser_id: campaignId } };
  return {
    queryKey: campaignsGetCampaignLayerParentQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsGetCampaignLayerParent({
        ...options,
        signal,
        throwOnError: true,
      });
      if (
        !isRecord(data) ||
        data.campaign_id !== campaignId ||
        !Array.isArray(data.ancestors) ||
        typeof data.can_leave !== 'boolean'
      )
        throw new Error('Where this campaign sits could not be read.');
      return data;
    },
  };
}

/** The tree's one read (FCM-BE63): the path down to this campaign and its default expansion. */
export function layerStructureOptions(
  campaignId: string,
): UseQueryOptions<P2pLayerStructureOut, Error> {
  const options = { path: { fundraiser_id: campaignId } };
  return {
    queryKey: campaignsGetCampaignLayerStructureQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsGetCampaignLayerStructure({
        ...options,
        signal,
        throwOnError: true,
      });
      if (
        !isRecord(data) ||
        data.campaign_id !== campaignId ||
        !Array.isArray(data.path) ||
        data.path.length === 0 ||
        !data.path.every(isNode) ||
        !Array.isArray(data.rows) ||
        !isRecord(data.under)
      )
        throw new Error('This campaign’s layers could not be read.');
      return { ...data, rows: data.rows.filter(isNode) };
    },
  };
}

/** One node's layers, a page at a time: opening a closed node, and "Show N more". */
export function layerNodePageOptions(
  campaignId: string,
  nodeId: string,
  offset: number,
): UseQueryOptions<P2pLayerNodeOut[], Error> {
  const options = {
    path: { fundraiser_id: campaignId, node_id: nodeId },
    query: { 'page[limit]': LAYER_PAGE, 'page[offset]': offset },
  };
  return {
    queryKey: campaignsListCampaignStructureLayersQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsListCampaignStructureLayers({
        ...options,
        signal,
        throwOnError: true,
      });
      return extractList<unknown>(data).filter(isNode);
    },
  };
}

/**
 * "A campaign you run" (FCM-BE63): the organization's open campaigns that could go below or above this
 * one, each with the attach's own answer; with `move`, the campaigns that layer could move under.
 */
export function layerCandidatesOptions(
  campaignId: string,
  offset: number,
  move?: string,
): UseQueryOptions<P2pLayerCandidateOut[], Error> {
  const options = {
    path: { fundraiser_id: campaignId },
    query: {
      'page[limit]': CANDIDATE_PAGE,
      'page[offset]': offset,
      ...(move ? { move } : {}),
    },
  };
  return {
    queryKey: campaignsListCampaignLayerCandidatesQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsListCampaignLayerCandidates({
        ...options,
        signal,
        throwOnError: true,
      });
      return extractList<P2pLayerCandidateOut>(data).filter(
        (row) => isRecord(row) && typeof row.campaign_id === 'string',
      );
    },
  };
}

function isInvite(value: unknown): value is P2pLayerInviteOut {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.campaign_id === 'string' &&
    typeof value.child_campaign_id === 'string' &&
    typeof value.status === 'string' &&
    isRecord(value.requires)
  );
}

/** The layer invitations this campaign made, newest first (FCM-BE34): the first page. */
export function layerInvitesMadeOptions(
  campaignId: string,
): UseQueryOptions<P2pLayerInviteOut[], Error> {
  const options = {
    path: { fundraiser_id: campaignId },
    query: { 'page[limit]': INVITE_PAGE, 'page[offset]': 0 },
  };
  return {
    queryKey: campaignsListSentCampaignLayerInvitesQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsListSentCampaignLayerInvites({
        ...options,
        signal,
        throwOnError: true,
      });
      return extractList<unknown>(data)
        .filter(isInvite)
        .filter((invite) => invite.campaign_id === campaignId);
    },
  };
}

/** The invitations this campaign has to join another, newest first (FCM-BE34): the first page. */
export function layerInvitationsOptions(
  campaignId: string,
): UseQueryOptions<P2pLayerInviteOut[], Error> {
  const options = {
    path: { fundraiser_id: campaignId },
    query: { 'page[limit]': INVITE_PAGE, 'page[offset]': 0 },
  };
  return {
    queryKey: campaignsListReceivedCampaignLayerInvitesQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsListReceivedCampaignLayerInvites({
        ...options,
        signal,
        throwOnError: true,
      });
      return extractList<unknown>(data)
        .filter(isInvite)
        .filter((invite) => invite.child_campaign_id === campaignId);
    },
  };
}

/** The public campaign read (`GET /campaigns/{id}`): its public total, goal and who picks its charities. */
export function publicCampaignOptions(campaignId: string): UseQueryOptions<P2pCampaignOut, Error> {
  const options = { path: { campaign_id: campaignId } };
  return {
    queryKey: p2pGetCampaignP2pQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.p2pGetCampaignP2P({ ...options, signal, throwOnError: true });
      if (!isRecord(data) || data.campaign_id !== campaignId)
        throw new Error('The public campaign did not match.');
      return data;
    },
  };
}

/** The public boards (`GET /campaigns/{id}/leaderboard`), for the card's Leaderboard preview. */
export function publicBoardsOptions(campaignId: string): UseQueryOptions<P2pLeaderboardOut, Error> {
  const options = { path: { campaign_id: campaignId } };
  return {
    queryKey: p2pGetLeaderboardQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.p2pGetLeaderboard({ ...options, signal, throwOnError: true });
      if (!isRecord(data) || data.campaign?.campaign_id !== campaignId)
        throw new Error('The public boards did not match.');
      return data;
    },
  };
}

/** The Schedule look's rows at a time (D-FCM-33 5.2: four dated games). */
export const SCHEDULE_ROWS = 4;
/** The most rows one schedule read serves (FCM-BE56's `page[limit]` cap): where the current game is looked for. */
export const SCHEDULE_PAGE = 100;

/**
 * The Schedule look's rows (FCM-BE56): four of the layers that count publicly, in the order they end, around
 * the current game (FCM-WEB62). The console previews no page under a game, so its current game is the first
 * not yet ended, the row the card marks on its own campaign's page (the donate card's `currentScheduleId`),
 * looked for in the schedule's first page; the four are then FCM-BE108's window `around` it. With no game
 * still to come in that page (a season that has ended, or one past its first 100 games: FCM-BE108's
 * residual, as on that page) the four are the first four. An anchor the schedule no longer counts answers
 * the campaign's 404, and the first four are read instead, as the donate card does.
 */
export interface ScheduleRead {
  /** The rows the card draws. */
  rows: P2pScheduleRowOut[];
  /**
   * How many rows the schedule counts in all, so a preview can say there are more: counted from its pages,
   * since the envelope's `meta.total_items` never reaches the module (the client keeps only `data`,
   * `agent_docs/api-client.md`). Null past the pages it counts.
   */
  total: number | null;
}

/** How many of the schedule's pages a preview counts before it no longer says how many rows there are. */
const SCHEDULE_COUNT_PAGES = 5;

export function scheduleOptions(campaignId: string): UseQueryOptions<ScheduleRead, Error> {
  const path = { campaign_id: campaignId };
  return {
    // The generated key of the first four, marked, since the entry holds the window around the current game.
    queryKey: [
      ...campaignsListCampaignScheduleQueryKey({ path, query: { 'page[limit]': SCHEDULE_ROWS } }),
      { around: 'current' },
    ],
    queryFn: async ({ signal }) => {
      const read = async (query: {
        'page[limit]': number;
        'page[offset]': number;
        around?: string;
      }) => {
        const { data } = await sdk.campaignsListCampaignSchedule({
          path,
          query,
          signal,
          throwOnError: true,
        });
        const served = extractList<P2pScheduleRowOut>(data);
        return {
          rows: served.filter((row) => isRecord(row) && typeof row.campaign_name === 'string'),
          count: served.length,
        };
      };
      const first = await read({ 'page[limit]': SCHEDULE_PAGE, 'page[offset]': 0 });
      // Every row the schedule serves, counted: the first page's, and the next page's while one comes back full.
      let counted = first.count;
      let pages = 1;
      while (counted === pages * SCHEDULE_PAGE && pages < SCHEDULE_COUNT_PAGES) {
        counted += (
          await read({ 'page[limit]': SCHEDULE_PAGE, 'page[offset]': pages * SCHEDULE_PAGE })
        ).count;
        pages += 1;
      }
      const total = counted === pages * SCHEDULE_PAGE ? null : counted;
      const current = first.rows.find((row) => !row.ended)?.campaign_id;
      if (current === undefined) return { rows: first.rows.slice(0, SCHEDULE_ROWS), total };
      try {
        const around = await read({
          'page[limit]': SCHEDULE_ROWS,
          'page[offset]': 0,
          around: current,
        });
        return { rows: around.rows, total };
      } catch (error) {
        if (!isGivmoError(error) || error.status !== 404) throw error;
        return {
          rows: (await read({ 'page[limit]': SCHEDULE_ROWS, 'page[offset]': 0 })).rows,
          total,
        };
      }
    },
  };
}

/** A fundraiser's byline: the organization or group that owns it, else the person who runs it. */
function bylineOf(data: FundraiserOut | undefined): string | null {
  if (!isRecord(data)) return null;
  const name =
    data.owner_company?.name ??
    data.charity_owner?.name ??
    data.owner_group?.name ??
    data.creator?.name ??
    null;
  return typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;
}

/**
 * The public fundraiser read of a campaign someone else runs: its byline, for "Organized by" and "From
 * {organization}" (frame 03f). Nothing while the page can't be read publicly (a draft, say). It is the
 * generated read with the byline picked by `select`, so the cache holds the read's own shape: another
 * reader of the same fundraiser by the same key (the employer module's Give page, in the company
 * console's one client) shares the answer instead of meeting a string.
 */
export function publicFundraiserOptions(
  fundraiserId: string,
): UseQueryOptions<
  FundraiserOut,
  FundraisersGetFundraiserError,
  string | null,
  ReturnType<typeof fundraisersGetFundraiserQueryKey>
> {
  return {
    ...fundraisersGetFundraiserOptions({ path: { fundraiser_id: fundraiserId } }),
    select: bylineOf,
  };
}

/**
 * Each generated op whose reads change when a campaign's layers do, by its query key's `_id`. Built on
 * first use, never at import: a host's tests may mock `@givmo/api-client/query` with only the ops they
 * read, and importing this package must not call the rest.
 */
let layerReadIds: Set<string> | null = null;
function layerReads(): Set<string> {
  layerReadIds ??= new Set(
    [
      campaignsGetCampaignLayerParentQueryKey({ path: { fundraiser_id: '' } }),
      campaignsGetCampaignLayerStructureQueryKey({ path: { fundraiser_id: '' } }),
      campaignsListCampaignStructureLayersQueryKey({ path: { fundraiser_id: '', node_id: '' } }),
      campaignsListCampaignLayerCandidatesQueryKey({ path: { fundraiser_id: '' } }),
      campaignsListSentCampaignLayerInvitesQueryKey({ path: { fundraiser_id: '' } }),
      campaignsListReceivedCampaignLayerInvitesQueryKey({ path: { fundraiser_id: '' } }),
    ].map((key) => key[0]._id),
  );
  return layerReadIds;
}

/**
 * Re-read every layer read the module holds. A structure write moves rows across several campaigns'
 * trees (the parent's, the child's, a move's two parents), so it re-reads them all, not one key.
 */
export function invalidateLayerReads(client: QueryClient) {
  const ids = layerReads();
  return client.invalidateQueries({
    predicate: (query) => {
      const head = query.queryKey[0];
      return isRecord(head) && typeof head._id === 'string' && ids.has(head._id);
    },
  });
}
